import React from 'react';
import { Skeleton } from 'src/components/ui/skeleton';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TimeSeriesDataPoint } from 'src/redux/admin/reporting/reporting.types';

interface MetricChartProps {
  title?: string;
  data: TimeSeriesDataPoint[];
  metrics: Array<{
    key: string;
    name: string;
    color: string;
  }>;
  isLoading: boolean;
}

const MetricChart: React.FC<MetricChartProps> = ({ title, data, metrics, isLoading }) => {
  const formatDateShort = (date: string) => {
    return new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  const formatDateFull = (date: string) => {
    return new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatMetricValue = (value: number, metricKey: string): string => {
    if (!isFinite(value) || isNaN(value)) {
      return 'N/A';
    }
    if (metricKey === 'ctr' || metricKey === 'video_completion_rate' || metricKey === 'audio_completion_rate') {
      return `${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(2)}%`;
    }
    if (metricKey === 'spend' || metricKey === 'cpa' || metricKey === 'cpm') {
      if (value >= 1000) {
        return `$${(value / 1000).toFixed(1)}k`;
      }
      return `$${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(2)}`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}k`;
    }
    return value.toString();
  };

  if (isLoading) {
    return (
      <div className="rounded-2xl bg-white border border-gray-400 p-4 lg:p-8 flex-1 min-w-[200px]">
        {title && (
          <div className="text-2xl font-light mb-4">
            {title}
          </div>
        )}
        <Skeleton className="w-full h-[300px]" />
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white border border-gray-400 p-4 lg:p-8 flex-1 min-w-[200px]">
      {title && (
        <div className="text-2xl font-light mb-4">
          {title}
        </div>
      )}
      <div className="h-[200px]">
        <ResponsiveContainer
          width="100%"
          height="100%"
        >
          <LineChart
            data={data}
            margin={{
              top: 10,
              right: metrics.length > 1 ? 0 : 20,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDateShort}
              minTickGap={30}
            />
            <YAxis
              yAxisId="left"
              tickFormatter={(value) => formatMetricValue(value, metrics[0].key)}
            />
            {metrics.length > 1 && (
              <YAxis
                yAxisId="right"
                orientation="right"
                tickFormatter={(value) => formatMetricValue(value, metrics[1].key)}
              />
            )}
            <Tooltip
              labelFormatter={formatDateFull}
              formatter={(value: number, name: string) => {
                const metric = metrics.find(m => m.name === name);
                if (!metric) return [value, name];
                return [formatMetricValue(value, metric.key), metric.name];
              }}
            />
            <Legend />
            {metrics.map((metric, index) => (
              <Line
                key={metric.key}
                type="monotone"
                dataKey={metric.key}
                name={metric.name}
                stroke={metric.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                yAxisId={index === 0 ? "left" : "right"}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default MetricChart;